import { useEffect, useRef, useCallback } from "react";
import { CheckIcon, FastForwardIcon } from "./Icons";

export interface SectionProgress {
  id: string;
  name: string;
  section_type: "conversation" | "form";
  done: boolean;
}

interface InterviewProgressTimelineProps {
  sections: SectionProgress[];
  currentSectionId: string | null;
  minimal?: boolean;
  className?: string;
}

/**
 * Progress dots indicator - shows completion state of each section
 */
function InterviewProgressDots({
  sections,
  currentSectionId,
}: {
  sections: SectionProgress[];
  currentSectionId: string | null;
}) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-2">
      {sections.map((section, i) => {
        const isCurrentSection = section.id === currentSectionId;
        const currentSectionIndex = sections.findIndex(
          (s) => s.id === currentSectionId,
        );
        const isPast = currentSectionIndex !== -1 && i < currentSectionIndex;

        // Determine classes based on state
        let dotClasses =
          "h-2 rounded-full transition-all duration-500 ease-out ";
        if (section.done) {
          dotClasses += "bg-primary ";
        } else if (isCurrentSection) {
          dotClasses += "bg-primary ";
        } else if (isPast) {
          dotClasses += "bg-primary/60 ";
        } else {
          dotClasses += "bg-muted-foreground/30 ";
        }

        dotClasses += isCurrentSection ? "w-6" : "w-2";

        return <div key={section.id} className={dotClasses} />;
      })}
    </div>
  );
}

/**
 * Interview progress timeline - horizontal scrolling sections with dots
 */
export function InterviewProgressTimeline({
  sections,
  currentSectionId,
  minimal = false,
  className,
}: InterviewProgressTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserScrollingRef = useRef(false);

  // Set container ref and scroll to current section on mount
  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;

      if (element && currentSectionId) {
        const currentItem = element.querySelector(
          `[data-section-id="${currentSectionId}"]`,
        ) as HTMLElement | null;

        if (currentItem) {
          const containerWidth = element.clientWidth;
          const itemLeft = currentItem.offsetLeft;
          const itemWidth = currentItem.clientWidth;
          const scrollLeft = itemLeft - containerWidth / 2 + itemWidth / 2;
          element.scrollLeft = scrollLeft;
        }
      }
    },
    [currentSectionId],
  );

  // Auto-scroll to current section
  useEffect(() => {
    if (isUserScrollingRef.current) return;

    const container = containerRef.current;
    if (!container || !currentSectionId) return;

    const currentItem = container.querySelector(
      `[data-section-id="${currentSectionId}"]`,
    ) as HTMLElement | null;

    if (!currentItem) return;

    const containerWidth = container.clientWidth;
    const itemLeft = currentItem.offsetLeft;
    const itemWidth = currentItem.clientWidth;
    const scrollLeft = itemLeft - containerWidth / 2 + itemWidth / 2;

    container.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [currentSectionId]);

  // Handle user scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      isUserScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 2000);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!sections || sections.length === 0) {
    return null;
  }

  const currentSection = sections.find((s) => s.id === currentSectionId);
  const isFormSection = currentSection?.section_type === "form";
  const showMinimal = isFormSection || minimal;

  return (
    <div className={`relative w-full ${className || ""}`}>
      {/* Scrollable container */}
      <div
        ref={setContainerRef}
        className="relative overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-none"
      >
        {/* Hide scrollbar */}
        <style>{`
          [data-timeline-scroll]::-webkit-scrollbar { display: none; }
        `}</style>

        {/* Horizontal timeline */}
        <div data-timeline-scroll className="flex">
          {sections.map((section, index) => {
            const isActive = section.id === currentSectionId;
            const isDone = section.done;
            const currentIndex = sections.findIndex(
              (s) => s.id === currentSectionId,
            );
            const isSkipped =
              currentIndex !== -1 && index < currentIndex && !isDone;

            return (
              <div
                key={section.id}
                data-section-id={section.id}
                className={`flex flex-col items-center justify-center gap-3 py-6 snap-center shrink-0 w-full transition-opacity duration-300 ${
                  !isDone && !isActive ? "opacity-40" : "opacity-100"
                }`}
              >
                {/* Section indicator */}
                <div className="flex flex-col items-center gap-3">
                  {/* Icon area - hide in minimal mode */}
                  {!showMinimal && (
                    <div className="relative flex items-center justify-center h-16 w-16">
                      {isDone && (
                        <CheckIcon className="w-8 h-8 text-green-600/60 stroke-[2.5]" />
                      )}
                      {isSkipped && (
                        <FastForwardIcon className="w-6 h-6 text-muted-foreground/40 stroke-2" />
                      )}
                    </div>
                  )}

                  {/* Section name */}
                  <span
                    className={`text-sm text-center transition-all duration-300 ${
                      isActive
                        ? "font-semibold text-foreground"
                        : "font-medium text-muted-foreground"
                    }`}
                  >
                    {section.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress dots - hide in minimal/form mode */}
      {!showMinimal && (
        <InterviewProgressDots
          sections={sections}
          currentSectionId={currentSectionId}
        />
      )}
    </div>
  );
}
